'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { 
  FiTruck, 
  FiTerminal,
  FiCode,
  FiCopy,
  FiZap,
  FiSend,
  FiInfo,
  FiCheckCircle,
  FiDownload,
  FiMapPin,
  FiEye,
  FiEyeOff,
  FiFileText
} from 'react-icons/fi'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import Swal from 'sweetalert2'
import { GUIAS_PRESETS, GUIA_DOCUMENTATION } from '@/lib/facturacion-constants'

export default function GuiasGenerarPage() {
  const [activeTab, setActiveTab] = useState('remitentePublico')
  const [token, setToken] = useState<string>('')
  const [showToken, setShowToken] = useState(false)
  const [loading, setLoading] = useState(false)
  const [requestBody, setRequestBody] = useState('')
  const [response, setResponse] = useState<any>(null)
  const [responseTime, setResponseTime] = useState<string | null>(null)

  useEffect(() => {
    fetchToken()
  }, [])

  useEffect(() => {
    if (GUIAS_PRESETS[activeTab]) {
      setRequestBody(JSON.stringify(GUIAS_PRESETS[activeTab], null, 4))
    }
  }, [activeTab])

  const fetchToken = async () => {
    try {
      const res = await fetch('/api/user/token')
      const data = await res.json()
      if (data.success) setToken(data.token)
    } catch (e) {
      console.error(e)
    }
  }

  const handleTest = async () => {
    if (!token) return Swal.fire('Error', 'Ingresa un token de acceso', 'error')
    
    // Validar que la claveSecreta sea válida
    try {
      const parsedBody = JSON.parse(requestBody)
      const claveSecreta = parsedBody.claveSecreta
      
      if (!claveSecreta || claveSecreta.trim() === '') {
        return Swal.fire({
          icon: 'error',
          title: 'Clave Secreta Requerida',
          html: 'Debes incluir el campo <strong>claveSecreta</strong> en el JSON para identificar la empresa.',
          confirmButtonColor: '#3b82f6'
        })
      }
      
      if (claveSecreta === 'tu-clave-secreta-aqui' || claveSecreta === 'TU_CLAVE' || claveSecreta === 'TU_CLAVE_SECRETA') {
        return Swal.fire({
          icon: 'warning',
          title: 'Clave Secreta Inválida',
          html: 'Reemplaza <code>TU_CLAVE</code> con la <strong>clave secreta real</strong> de tu empresa.<br><br>Puedes encontrarla en <strong>Gestión de Empresas → Configurar</strong>.',
          confirmButtonColor: '#3b82f6'
        })
      }
    } catch (e) {
      return Swal.fire({
        icon: 'error',
        title: 'JSON Inválido',
        text: 'El JSON del payload tiene errores de sintaxis. Revísalo antes de continuar.',
        confirmButtonColor: '#3b82f6'
      })
    }
    
    setLoading(true)
    setResponse(null)
    setResponseTime(null)

    const startTime = performance.now()
    const endpoint = activeTab === 'remitentePublico' 
      ? 'https://miapi.cloud/apifact/despatch/remitent' 
      : 'https://miapi.cloud/apifact/despatch/transport'

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.trim()}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      })
      const data = await res.json()
      const endTime = performance.now()
      
      const timeSecs = (endTime - startTime) / 1000
      setResponseTime(timeSecs < 1 ? `${(timeSecs * 1000).toFixed(0)}ms` : `${timeSecs.toFixed(2)}s`)
      
      // La visualización del JSON (setResponse) se mantiene fiel a lo que devuelve la API
      setResponse(data)

      if (res.ok) {
        fetch('/api/user/record', { method: 'POST' }).catch(err => console.error(err))
      }

      if (res.ok) {
        fetch('/api/user/record', { method: 'POST' }).catch(err => console.error(err))
      }
    } catch (e: any) {
      setResponse({ error: 'Error al conectar con la API', details: e.message })
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = (text: string, label: string = 'Copiado') => {
    navigator.clipboard.writeText(text)
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: `${label} al portapapeles`,
      showConfirmButton: false,
      timer: 1500
    })
  }

  // Función para obtener la documentación de los campos presentes en el JSON (recursiva)
  const getFlattenedDocs = () => {
    try {
      const json = JSON.parse(requestBody)
      const result: { key: string; desc: string; type: string }[] = []
      
      const process = (obj: any, path = '') => {
        if (!obj || typeof obj !== 'object') return
        
        Object.keys(obj).forEach(key => {
          const fullPath = path ? `${path}.${key}` : key
          const value = obj[key]
          
          let typeStr: string = typeof value
          if (Array.isArray(value)) typeStr = 'Array'
          else if (value === null) typeStr = 'null'

          if (GUIA_DOCUMENTATION[key]) {
            result.push({ key: fullPath, desc: GUIA_DOCUMENTATION[key], type: typeStr })
          }

          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            process(value, fullPath)
          } else if (Array.isArray(value) && value.length > 0) {
            process(value[0], `${fullPath}[0]`)
          }
        })
      }
      
      process(json)
      return result
    } catch (e) {
      return []
    }
  }

  const detailedDocs = getFlattenedDocs()

  return (
    <div className="max-w-[1700px] mx-auto space-y-8 pb-20 animate-in fade-in duration-700 font-sans">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-50">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-[11px] font-bold uppercase tracking-wider">
            <FiTruck className="animate-pulse" /> Guías de Remisión UBL 2.1
          </div>
          <h1 className="text-4xl font-[900] text-gray-900 tracking-tight leading-none">Generar Guías XML</h1>
          <p className="text-gray-500 max-w-xl text-md font-medium">
            Crea Guías Remitente y Transportista en tiempo real con validación SUNAT.
          </p>
        </div>
        
        <div className="flex flex-col gap-3">
          <div className="relative group">
            <div className="absolute -top-2.5 left-4 px-2 bg-white text-[10px] font-[800] text-gray-400 uppercase tracking-[0.2em]">
              Access Token
            </div>
            <div className="flex items-center bg-gray-50/50 border-2 border-gray-100 rounded-2xl overflow-hidden focus-within:border-indigo-500 focus-within:ring-4 focus-within:ring-indigo-50 transition-all shadow-sm">
              <input 
                type={showToken ? 'text' : 'password'}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Bearer token..."
                className="w-full sm:w-80 px-5 py-4 bg-transparent text-sm font-mono outline-none"
              />
              <div className="flex items-center px-3 border-l border-gray-200 bg-white/30">
                <button onClick={() => setShowToken(!showToken)} className="p-2 text-gray-400 hover:text-indigo-600 transition-colors">
                  {showToken ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
                <button onClick={() => copyToClipboard(token, 'Token')} className="p-2 text-gray-400 hover:text-indigo-600 transition-colors">
                  <FiCopy size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
        <TabsList className="bg-white p-2 rounded-[1.5rem] shadow-sm border border-gray-50 h-auto flex gap-2 w-max">
          <TabsTrigger 
            value="remitentePublico"
            className="rounded-xl px-8 py-3 text-xs font-black uppercase tracking-wide data-[state=active]:bg-indigo-600 data-[state=active]:text-white transition-all flex items-center gap-2"
          >
            <FiMapPin /> Remitente
          </TabsTrigger>
          <TabsTrigger 
            value="transportista"
            className="rounded-xl px-8 py-3 text-xs font-black uppercase tracking-wide data-[state=active]:bg-indigo-600 data-[state=active]:text-white transition-all flex items-center gap-2"
          >
            <FiTruck /> Transportista
          </TabsTrigger>
        </TabsList>

        <div className="grid grid-cols-1 space-y-8">
          {/* Fila Superior: Payload y Guía Técnica */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Editor Payload */}
            <Card className="border-none shadow-2xl rounded-[2.5rem] overflow-hidden bg-[#0f172a] flex flex-col h-[700px]">
              <CardHeader className="border-b border-white/5 py-5 px-8 flex flex-row items-center justify-between bg-[#1e293b]/30 flex-none">
                <div className="flex items-center gap-3">
                  <FiTerminal className="text-indigo-400 text-xl" />
                  <CardTitle className="text-base font-extrabold text-white">UBL Payload</CardTitle>
                </div>
                <div className="flex items-center gap-3">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="rounded-xl border-indigo-400 text-indigo-400 hover:bg-indigo-500/10 font-bold gap-2">
                        <FiCode /> Detalle Técnico
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col rounded-[2rem] border-none shadow-2xl">
                      <DialogHeader className="p-8 bg-indigo-600 text-white flex-none">
                        <DialogTitle className="text-2xl font-black flex items-center gap-3">
                          <FiTerminal /> Diccionario Técnico (Guías)
                        </DialogTitle>
                        <p className="text-indigo-100 text-sm mt-1">Campos requeridos para la generación de Guías de Remisión UBL 2.1.</p>
                      </DialogHeader>
                      <div className="flex-grow overflow-y-auto p-8 custom-scrollbar bg-gray-50">
                        <div className="grid grid-cols-1 gap-4">
                          {detailedDocs.map((doc, i) => (
                            <div key={i} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                              <div className="flex items-center justify-between mb-2">
                                <code className="text-indigo-600 font-black text-sm">{doc.key}</code>
                                <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                  {doc.type}
                                </span>
                              </div>
                              <p className="text-gray-600 text-sm leading-relaxed">{doc.desc}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard(requestBody)} className="text-gray-400 hover:text-white rounded-xl">
                    <FiCopy />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 relative flex-grow overflow-hidden">
                <textarea 
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                  spellCheck={false}
                  className="w-full h-full p-8 font-mono text-[13px] bg-transparent text-indigo-300 focus:outline-none resize-none leading-relaxed custom-scrollbar-dark"
                />
                <div className="absolute bottom-8 right-8">
                   <Button 
                    onClick={handleTest}
                    disabled={loading}
                    className="bg-indigo-600 hover:bg-indigo-500 text-white px-10 py-7 rounded-2xl font-black text-sm shadow-xl gap-3 transition-all active:scale-95 border-b-4 border-indigo-800"
                  >
                    {loading ? (
                      <div className="h-5 w-5 border-3 border-white/20 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <FiSend /> GENERAR GUÍA {activeTab === 'remitentePublico' ? 'REMITENTE' : 'TRANSPORTISTA'}
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Guía Técnica Dinámica */}
            <Card className="border-none shadow-xl rounded-[2.5rem] bg-white overflow-hidden flex flex-col h-[700px] border border-gray-50">
              <CardHeader className="bg-gray-50/50 border-b py-5 px-8 flex-none">
                <div className="flex items-center gap-3">
                  <FiInfo className="text-indigo-600 text-xl" />
                  <CardTitle className="text-base font-extrabold text-gray-800">
                    Estructura Guía {activeTab === 'remitentePublico' ? 'Remitente' : 'Transportista'}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-0 flex flex-col flex-grow overflow-hidden">
                <div className="flex-grow overflow-y-auto px-8 py-6 space-y-4 custom-scrollbar">
                  {detailedDocs.length > 0 ? (
                    detailedDocs.map(({ key, desc }) => (
                      <div key={key} className="p-5 rounded-2xl border border-gray-50 bg-gray-50/30 hover:bg-indigo-50/30 hover:border-indigo-100 transition-all group">
                        <div className="flex items-center justify-between mb-1">
                          <code className="text-indigo-600 font-black text-xs">{key}</code>
                          <span className="text-[10px] text-gray-300 font-bold group-hover:text-indigo-400">Guía Field</span>
                        </div>
                        <p className="text-[13px] text-gray-500 font-medium leading-relaxed">{desc}</p>
                      </div>
                    ))
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-300 italic text-sm text-center px-10">
                       <FiInfo size={40} className="mb-4 opacity-10" />
                       No hay documentación técnica para los campos actuales del JSON.
                    </div>
                  )}
                </div>
                <div className="p-8 bg-indigo-900 text-white flex-none flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-black uppercase text-indigo-300 mb-1 tracking-widest">Environment URL</p>
                    <code className="text-[11px] opacity-70 font-mono">
                      {activeTab === 'remitentePublico' ? '.../apifact/despatch/remitent' : '.../apifact/despatch/transport'}
                    </code>
                  </div>
                  <FiCheckCircle className="text-green-400 text-2xl" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fila Inferior: Resultado API */}
          <Card className={`w-full border-none transition-all duration-700 rounded-[2.5rem] overflow-hidden ${response ? 'shadow-2xl shadow-indigo-100 border-2 border-indigo-100' : 'shadow-xl bg-white border border-gray-100'}`}>
             <CardHeader className="py-6 px-10 border-b border-gray-50 flex flex-row items-center justify-between bg-white flex-none">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${response?.success ? 'bg-green-100 text-green-600 border border-green-200' : 'bg-gray-100 text-gray-400 border border-gray-200'}`}>
                    <FiCode className="text-xl" />
                  </div>
                  <div>
                    <CardTitle className="text-lg font-extrabold text-gray-800 tracking-tight">Resultado de Operación</CardTitle>
                    <div className="flex items-center gap-3 mt-1">
                       {responseTime && <span className="text-[10px] font-black px-4 py-1.5 bg-gray-50 text-gray-500 rounded-full border border-gray-100">{responseTime}</span>}
                       {response && (
                          <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${
                            (response.success || response.respuesta?.success) ? 'text-green-500' : 'text-red-500'
                          }`}>
                            {(response.success || response.respuesta?.success) ? 'GUÍA GENERADA' : 'FALLA EN DATOS'}
                          </span>
                        )}
                    </div>
                  </div>
                </div>
             </CardHeader>
             <CardContent className="p-10 bg-white">
                {response ? (
                  (() => {
                    // Normalizar internamente para los botones y mensajes
                    const resData = response.respuesta ? { ...response, ...response.respuesta } : response;
                    const pdf_a4 = resData.pdf_a4 || resData['pdf-a4'];
                    const xml_firmado = resData.xml_firmado || resData['xml-firmado'];
                    const isSuccess = resData.success === true;

                    return (
                      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-8 bg-[#0f172a] p-8 rounded-[2.5rem] shadow-inner relative group">
                           <pre className="text-indigo-400 text-[13px] font-mono overflow-auto max-h-[350px] custom-scrollbar-dark leading-relaxed">
                              {JSON.stringify(response, null, 2)}
                           </pre>
                        </div>
                        <div className="lg:col-span-4 space-y-6 self-center text-center">
                           {isSuccess ? (
                             <>
                                <div className="p-8 rounded-[2rem] bg-indigo-50 border border-indigo-100 space-y-4">
                                   <div className="w-16 h-16 bg-indigo-600 text-white rounded-3xl flex items-center justify-center mx-auto shadow-xl shadow-indigo-200">
                                      <FiCheckCircle size={32} />
                                   </div>
                                   <h4 className="font-black text-indigo-900 text-xl">¡Guía XML Lista!</h4>
                                   <p className="text-sm text-indigo-700 font-medium">El archivo ha sido firmado y validado por SUNAT.</p>
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                  {pdf_a4 && (
                                    <a href={pdf_a4} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 py-5 bg-white border-2 border-gray-100 hover:border-indigo-600 hover:text-indigo-600 rounded-2xl text-[13px] font-black transition-all shadow-sm group">
                                      <FiFileText className="text-lg group-hover:scale-110 transition-transform" /> VER GUÍA (PDF)
                                    </a>
                                  )}
                                  {xml_firmado && (
                                    <a href={xml_firmado} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 py-5 bg-white border-2 border-gray-100 hover:border-indigo-600 hover:text-indigo-600 rounded-2xl text-[13px] font-black transition-all shadow-sm group">
                                      <FiDownload className="text-lg group-hover:translate-y-0.5 transition-transform" /> BAJAR XML FIRMADO
                                    </a>
                                  )}
                                </div>
                             </>
                           ) : (
                             <div className="p-10 rounded-[2.5rem] bg-red-50 border border-red-100 space-y-4">
                               <div className="w-16 h-16 bg-red-500 text-white rounded-3xl flex items-center justify-center mx-auto">
                                  <FiInfo size={32} />
                               </div>
                               <h4 className="font-black text-red-900 text-xl">Error de Emisión</h4>
                               <p className="text-sm text-red-700 font-medium">{resData.message || resData.mensaje || "Verifica los datos de traslado y destinatario."}</p>
                             </div>
                           )}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="py-24 flex flex-col items-center justify-center text-gray-300 gap-6 border-2 border-dashed border-gray-100 rounded-[3rem]">
                    <div className="w-24 h-24 rounded-[2.5rem] bg-gray-50 flex items-center justify-center shadow-inner">
                      <FiTruck size={40} className="opacity-10" />
                    </div>
                    <div className="text-center space-y-2">
                       <p className="text-[12px] font-black uppercase tracking-[0.4em] opacity-40">Sin Procesar</p>
                       <p className="text-sm font-medium text-gray-400">Haz clic en el botón superior para realizar la prueba</p>
                    </div>
                  </div>
                )}
             </CardContent>
          </Card>
        </div>
      </Tabs>

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        
        .custom-scrollbar-dark::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar-dark::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar-dark::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; }
      `}</style>
    </div>
  )
}
